<template>
  <div class="favorites-panel">
    <div class="header">
      <TextBody tag="span" i18n-key="common.saved" weight="medium" :body-type="2"></TextBody>
      <span class="count">{{ '(' + favoriteDocumentsCount + ')' }} </span>
      <BbBtn
        v-if="favoriteDocumentsCount > 0"
        class="delete-all-button"
        size="small"
        type="secondary"
        :is-disabled="isDataLoading"
        @click="openConfirmPopup"
      >
        <TextBody i18n-key="modals.deleteAll"></TextBody>
      </BbBtn>
      <BbBtn
        class="close-button"
        size="x-small"
        type="icon"
        icon="x-small"
        icon-size="medium"
        no-background
        @click="onClose"
      ></BbBtn>
    </div>
    <div v-if="favoriteDocumentsCount === 0" class="empty-message">
      <TextBody tag="span" i18n-key="favorites.noSavedDocuments" weight="medium" :body-type="2"></TextBody>
      <TextBody tag="span" i18n-key="favorites.savedDocumentsAppearHere" :body-type="2"></TextBody>
    </div>
    <div class="documents-list" v-if="!isDataLoading">
      <FavoriteDocumentCard
        v-for="(reference, index) in favoriteDocuments"
        :key="reference.externalId || reference"
        :reference="reference"
        :index="index"
        @moveDocument="moveDocument"
      />
    </div>
    <FavoritesSkeletonLoader v-else />
  </div>
  <WarningPopup
    :popup-opened="confirmPopupOpened"
    title-key="favorites.deleteSavedDocuments"
    :warning-message="warningMessage"
    confirm-text-key="modals.confirmDeleteText"
    @update:open="confirmPopupOpened = false"
    @execute:action="deleteAll"
  />
</template>

<script>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n/dist/vue-i18n';
import TextBody from '~/components/common/TextBody';
import useModals from '~/composables/useModals';
import useCollections from '~/composables/useCollections';
import FavoriteDocumentCard from '~/components/conversation/favoritesPanel/FavoriteDocumentCard';
import { COLLECTIONS } from '~/constants/collections';
import FavoritesSkeletonLoader from '~/components/conversation/favoritesPanel/FavoritesSkeletonLoader';
import WarningPopup from '~/components/common/WarningPopup';

export default {
  components: { WarningPopup, FavoritesSkeletonLoader, FavoriteDocumentCard, TextBody },

  setup() {
    const { t } = useI18n();
    const {
      favoriteDocuments,
      favoriteDocumentsCount,
      reorderFavorites,
      deleteAllDocuments,
      setAdditionalDocumentsData,
    } = useCollections();
    const { setCurrentDocumentsDialog } = useModals();

    const confirmPopupOpened = ref(false);

    const isDataLoading = ref(false);
    const warningMessage = computed(() => t('favorites.warning', { count: favoriteDocumentsCount.value }));

    const onClose = () => {
      setCurrentDocumentsDialog(null);
    };

    const openConfirmPopup = () => {
      confirmPopupOpened.value = true;
    };

    const moveDocument = (fromIndex, toIndex) => {
      if (toIndex !== fromIndex) {
        const documents = favoriteDocuments.value;
        const movingDocument = documents.splice(fromIndex, 1)[0];
        documents.splice(toIndex, 0, movingDocument);
        reorderFavorites(documents);
      }
    };

    const deleteAll = () => {
      confirmPopupOpened.value = false;
      deleteAllDocuments();
    };

    onMounted(async () => {
      isDataLoading.value = true;
      await setAdditionalDocumentsData(COLLECTIONS.favorites);
      isDataLoading.value = false;
    });

    return {
      deleteAll,
      favoriteDocumentsCount,
      favoriteDocuments,
      confirmPopupOpened,
      isDataLoading,
      openConfirmPopup,
      moveDocument,
      onClose,
      warningMessage,
    };
  },
};
</script>

<style scoped lang="scss">
* {
  box-sizing: border-box;
}

.favorites-panel {
  @include media-breakpoint-up('sm') {
    display: flex;
  }

  max-height: calc(100vh - 80px);

  // take into consideration the margins
  height: 100%;
  display: none;
  flex-direction: column;
  background-color: $bb-neutral-6;
  border-radius: $bb-spacing-8;
  border: 1px solid #cecdcb;
  box-shadow: 0 0 $bb-spacing-2 0 #0000001a;
  align-items: center;
  min-width: 348px;
  max-width: 348px;
  padding: $bb-spacing-4;
  overflow: auto;
  margin: auto;

  .empty-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    justify-content: center;

    :deep(span:first-of-type) {
      font-size: $bb-spacing-6;
      line-height: $bb-spacing-7;
    }
  }

  .header {
    .count {
      @include bb-regular-text-styles('lg');
      font-weight: 500;
    }

    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    gap: $bb-spacing-2;
    margin-bottom: $bb-spacing-6;

    .delete-all-button {
      color: $bb-interface-5 !important;
      border: 1px solid $bb-interface-5;
    }

    .close-button {
      margin-left: auto; /* This pushes the third item (button) to the end of the flex container */
    }
  }

  .documents-list {
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;
    width: 100%;
  }
}
</style>
